import { Link } from "react-router-dom";
import { clockTime, dayLabel, inlineFields, jsonTokens, levelTone } from "../../lib/logs";
import type { JsonTokenKind } from "../../lib/logs";
import { TONE_TEXT } from "../../lib/status";
import type { AppLogEntry } from "../../lib/types";
import { CopyButton } from "../ui/Copy";

/**
 * One line of the server's log, laid out on a fixed grid so a screen of them
 * reads down the columns: time, level, station, message. Monospace, because the
 * values in a log — ids, payload keys, numbers — are read character by character.
 *
 * Collapsed, it is what pino-pretty printed. Expanded, it is the whole record as
 * pino wrote it, which is the thing to paste into a ticket.
 */

/**
 * Phone: time and level share the first line and the message takes the full
 * width under them. From `md` the three sit side by side; the station gets its
 * own column only from `lg`, because most lines have none and below that width
 * the column would cost the message a third of the row. There the station leads
 * the message instead.
 */
const GRID =
  "grid grid-cols-[6.5rem_minmax(0,1fr)] md:grid-cols-[6.5rem_3.75rem_minmax(0,1fr)] lg:grid-cols-[6.5rem_3.75rem_8rem_minmax(0,1fr)]";

export function LogDay({ iso }: { readonly iso: string }) {
  return (
    <li className="sticky top-0 z-10 border-b border-rule-strong bg-paper pt-5 pb-1.5">
      <span className="text-micro font-semibold uppercase text-ink">{dayLabel(iso)}</span>
    </li>
  );
}

export function LogLine({
  entry,
  isExpanded,
  onToggle,
  stationHref,
}: {
  readonly entry: AppLogEntry;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  /** This page, narrowed to one station with every other filter kept. */
  readonly stationHref: (cpId: string) => string;
}) {
  const tone = levelTone(entry.level);
  const isLoud = tone === "faulted";
  const fields = inlineFields(entry.data);

  return (
    <li
      className={`group border-b border-rule font-mono text-data ${isLoud ? "bg-state-faulted-wash/60" : ""}`}
    >
      <div className={`${GRID} items-start gap-x-3 gap-y-0.5 py-1.5`}>
        <time
          dateTime={entry.ts}
          title={new Date(entry.ts).toLocaleString()}
          className="tabular-nums text-ink-faint"
        >
          {clockTime(entry.ts)}
        </time>
        <span className={`text-micro font-semibold uppercase ${TONE_TEXT[tone]}`}>
          {entry.level}
        </span>
        <span className="hidden truncate lg:block">
          {entry.cpId === null ? (
            <span className="text-ink-faint">·</span>
          ) : (
            <StationLink cpId={entry.cpId} href={stationHref(entry.cpId)} />
          )}
        </span>
        <div className="col-span-2 min-w-0 md:col-span-1">
          {entry.cpId === null ? null : (
            <span className="mr-2 lg:hidden">
              <StationLink cpId={entry.cpId} href={stationHref(entry.cpId)} />
            </span>
          )}
          <button
            type="button"
            aria-expanded={isExpanded}
            onClick={onToggle}
            className="inline cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-signal"
          >
            <span className={`wrap-anywhere ${isLoud ? "text-signal-ink" : "text-ink"}`}>
              {entry.msg === "" ? <span className="text-ink-faint">(no message)</span> : entry.msg}
            </span>
            {fields.length === 0 ? null : (
              <span className="ml-2 break-all text-ink-faint">
                {fields.map(([key, value]) => (
                  <span key={key} className="mr-2 inline-block">
                    <span className="text-ink-muted">{key}</span>={value}
                  </span>
                ))}
              </span>
            )}
            {entry.imported ? (
              <span className="ml-2 text-micro uppercase text-ink-faint" title="From Docker's log">
                imported
              </span>
            ) : null}
          </button>
        </div>
      </div>
      {isExpanded ? <LogRecord entry={entry} /> : null}
    </li>
  );
}

function StationLink({ cpId, href }: { readonly cpId: string; readonly href: string }) {
  return (
    <Link
      to={href}
      className="text-ink underline decoration-rule underline-offset-2 hover:decoration-signal"
      title={`Only ${cpId}`}
    >
      {cpId}
    </Link>
  );
}

const TOKEN_CLASS: Readonly<Record<JsonTokenKind, string>> = {
  key: "text-ink",
  string: "text-state-available",
  number: "text-state-charging",
  literal: "text-state-transitional",
  punctuation: "text-ink-faint",
};

function LogRecord({ entry }: { readonly entry: AppLogEntry }) {
  const record = { time: entry.ts, level: entry.level, msg: entry.msg, ...entry.data };
  const pretty = JSON.stringify(record, null, 2);

  return (
    <div className="mb-3 ml-0 flex flex-col gap-2 border-l-2 border-rule-strong bg-paper-sunken py-3 pr-3 pl-4 md:ml-[7.25rem]">
      <pre className="m-0 overflow-x-auto font-mono text-data whitespace-pre-wrap break-all">
        {jsonTokens(record).map((token, index) => (
          // Index keys are right here: the list is derived, never reordered.
          <span key={index} className={TOKEN_CLASS[token.kind]}>
            {token.text}
          </span>
        ))}
      </pre>
      <div>
        <CopyButton value={pretty} what="This log record" variant="ghost">
          Copy record
        </CopyButton>
      </div>
    </div>
  );
}
